/**
 * ElderLeo — Grand Elder Leo, the final Cat Elder (Babylon.js).
 *
 * A cream-tabby in a green argyle sweater. Majestic, lucid, with
 * immense wizard energy. Teaches the "Wisdom of Integration" from
 * atop the Clockwork Canopy and delivers the signature closing stanza
 * that frames the end of the game.
 *
 * Appears after the Canopy Apex puzzle in a cinematic finale scene.
 * Primarily visual — interaction is driven by the CutsceneSystem.
 *
 * Visual: a warm-cream capsule wrapped in a soft halo of brass + gold
 * point-light glow (larger range and steadier intensity than Elder
 * Kitty in the Scrapyard), plus a gentle idle bob.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { COLORS } from "../../config/GameConfig";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";

// TODO(art): swap for new Grand Elder Leo GLB — cream-tabby wizard in a
// green argyle sweater. Reusing the placeholder Kitty mesh for now; a
// dedicated model will land in a follow-up art PR.
const ELDER_LEO_MODEL = "/assets/models/npcs/elder_leo_stylized.glb";

export class ElderLeo {
  public mesh: Mesh;
  private material: StandardMaterial;
  private glowLight: PointLight;
  private baseY: number;
  private visualModel: InstantiatedModel | null = null;

  constructor(scene: Scene, position: Vector3) {
    this.baseY = position.y;

    // Body — warm cream capsule (visual fallback until GLB loads).
    // Grand Elder Leo reads as almost luminous: cream fur against a
    // green argyle sweater, haloed in gold.
    this.mesh = MeshBuilder.CreateCapsule(
      "grand_elder_leo",
      { height: 1.6, radius: 0.4, tessellation: 16, subdivisions: 6 },
      scene,
    );
    this.mesh.position = position.clone();

    this.material = new StandardMaterial("grand_elder_leo_mat", scene);
    // Cream tabby body tone; sweater green is implied by the surrounding
    // rim-light from the glow layer and point light below.
    this.material.diffuseColor = Color3.FromHexString('#f2d9a6');
    this.material.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.25);
    this.material.specularColor = Color3.Black();
    this.mesh.material = this.material;

    // Big, warm wizard halo — larger and steadier than Elder Kitty's.
    this.glowLight = new PointLight("grand_elder_leo_glow", Vector3.Zero(), scene);
    this.glowLight.diffuse = Color3.FromHexString(COLORS.BRASS);
    this.glowLight.specular = Color3.FromHexString(COLORS.COPPER);
    this.glowLight.intensity = 4.0;
    this.glowLight.range = 12;
    this.glowLight.parent = this.mesh;
    this.glowLight.position = new Vector3(0, 0.6, 0);

    // Fire-and-forget GLB load. Capsule stays in the graph as the
    // parent transform so the orbiting glow light and bob anim still
    // drive the authored mesh.
    getAssetManager()
      .instantiateModel(scene, ELDER_LEO_MODEL)
      .then((model) => {
        if (this.mesh.isDisposed()) {
          model.dispose();
          return;
        }
        this.visualModel = model;
        for (const root of model.rootNodes) {
          if (root instanceof TransformNode) {
            root.parent = this.mesh;
            root.position.copyFromFloats(0, 0, 0);
            root.scaling.setAll(1.0);
            root.computeWorldMatrix(true);
            for (const mesh of model.meshes) {
              mesh.computeWorldMatrix(true);
            }
            const bb = root.getHierarchyBoundingVectors(true);
            const targetBottom = this.mesh.position.y - 0.8;
            root.position.y = targetBottom - bb.min.y;
          }
        }
        for (const m of model.meshes) m.isPickable = false;
        this.mesh.isVisible = false;
      })
      .catch((err) => {
        console.warn("[ElderLeo] Failed to load model", ELDER_LEO_MODEL, err);
      });
  }

  /** Gentle idle bob animation — call each frame. */
  update(time: number, _deltaSeconds: number): void {
    this.mesh.position.y = this.baseY + Math.sin(time) * 0.04;

    // Wide, slow wizard orbit — theatrical but unhurried.
    const orbitRadius = 1.0;
    this.glowLight.position.x = Math.sin(time * 0.5) * orbitRadius;
    this.glowLight.position.z = Math.cos(time * 0.5) * orbitRadius;

    // Steady wizardly pulse, higher baseline than Elder Kitty
    this.glowLight.intensity = 3.5 + Math.sin(time * 1.2) * 0.5;
  }

  dispose(): void {
    this.visualModel?.dispose();
    this.visualModel = null;
    this.glowLight.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}
